import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { GeoPoint, PlaybackState, UseMapPlaybackReturn } from '../types/tracking';
import { interpolatePosition, haversineDistance } from '../utils/mapHelpers';

const FRAME_DURATION_MS = 1000; // Base duration per point at 1x

const initialState: PlaybackState = {
  isPlaying: false,
  speed: 1,
  currentIndex: 0,
  currentPosition: null,
  currentTimestamp: null,
  totalPoints: 0,
  distanceCoveredKm: 0,
  totalDistanceKm: 0,
  startTime: null,
  endTime: null,
};

/**
 * Pre-compute cumulative distance (km) at each point index. Skips the edge
 * between two points that belong to different movement segments — there is
 * no recorded track there, so adding the haversine bridge would inflate
 * the total beyond what the server reports.
 */
function buildCumulativeDistances(points: GeoPoint[]): number[] {
  const distances = [0];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (
      prev.segment_index !== undefined &&
      curr.segment_index !== undefined &&
      prev.segment_index !== curr.segment_index
    ) {
      distances.push(distances[i - 1]);
      continue;
    }
    const d = haversineDistance(
      prev.latitude, prev.longitude,
      curr.latitude, curr.longitude
    );
    distances.push(distances[i - 1] + d);
  }
  return distances;
}

export function useMapPlayback(
  points: GeoPoint[],
  /**
   * Server-authoritative total distance in meters (sum of
   * `total_distance_meters` across the user's movement_segments for the
   * day). When provided, it overrides the haversine total — the Reports
   * page uses this same field and we want the two surfaces to agree.
   */
  totalDistanceMetersOverride?: number,
): UseMapPlaybackReturn {
  const [state, setState] = useState<PlaybackState>(initialState);

  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const progressRef = useRef<number>(0);
  const currentIndexRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  const speedRef = useRef<number>(1);

  // Pre-compute distances
  const cumulativeDistances = useMemo(() => buildCumulativeDistances(points), [points]);
  // Prefer the server's authoritative total when supplied; otherwise fall
  // back to the cumulative haversine sum (legacy behaviour for callers that
  // don't pass the override).
  const totalDistanceKm =
    totalDistanceMetersOverride !== undefined
      ? totalDistanceMetersOverride / 1000
      : cumulativeDistances.length > 0
      ? cumulativeDistances[cumulativeDistances.length - 1]
      : 0;

  // Reset when points change
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    isPlayingRef.current = false;
    currentIndexRef.current = 0;
    progressRef.current = 0;
    lastTimeRef.current = 0;

    setState({
      ...initialState,
      totalPoints: points.length,
      totalDistanceKm,
      currentPosition: points.length > 0 ? [points[0].latitude, points[0].longitude] : null,
      currentTimestamp: points.length > 0 ? points[0].recorded_at : null,
      startTime: points.length > 0 ? points[0].recorded_at : null,
      endTime: points.length > 0 ? points[points.length - 1].recorded_at : null,
    });
  }, [points, totalDistanceKm]);

  const animate = useCallback(
    (timestamp: number) => {
      if (!isPlayingRef.current) return;

      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const elapsed = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const frameDuration = FRAME_DURATION_MS / speedRef.current;
      progressRef.current += elapsed / frameDuration;

      let idx = currentIndexRef.current;
      while (progressRef.current >= 1 && idx < points.length - 1) {
        progressRef.current -= 1;
        idx++;
      }

      currentIndexRef.current = idx;

      // Reached end
      if (idx >= points.length - 1) {
        isPlayingRef.current = false;
        setState(prev => ({
          ...prev,
          isPlaying: false,
          currentIndex: points.length - 1,
          currentPosition: [points[points.length - 1].latitude, points[points.length - 1].longitude],
          currentTimestamp: points[points.length - 1].recorded_at,
          distanceCoveredKm: totalDistanceKm,
        }));
        return;
      }

      const t = Math.min(progressRef.current, 1);
      const p1: [number, number] = [points[idx].latitude, points[idx].longitude];
      const p2: [number, number] = [points[idx + 1].latitude, points[idx + 1].longitude];
      const pos = interpolatePosition(p1, p2, t);

      // Interpolate distance
      const segmentDist = cumulativeDistances[idx + 1] - cumulativeDistances[idx];
      const distCovered = cumulativeDistances[idx] + segmentDist * t;

      setState(prev => ({
        ...prev,
        currentIndex: idx,
        currentPosition: pos,
        currentTimestamp: points[idx].recorded_at,
        distanceCoveredKm: distCovered,
      }));

      rafRef.current = requestAnimationFrame(animate);
    },
    [points, cumulativeDistances, totalDistanceKm]
  );

  const play = useCallback(() => {
    if (points.length < 2) return;

    // If at end, restart
    if (currentIndexRef.current >= points.length - 1) {
      currentIndexRef.current = 0;
      progressRef.current = 0;
    }

    isPlayingRef.current = true;
    lastTimeRef.current = 0;
    speedRef.current = state.speed; // Sync from latest state

    setState(prev => ({ ...prev, isPlaying: true }));
    rafRef.current = requestAnimationFrame(animate);
  }, [points, animate, state.speed]);

  const pause = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    isPlayingRef.current = false;
    setState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    isPlayingRef.current = false;
    currentIndexRef.current = 0;
    progressRef.current = 0;
    lastTimeRef.current = 0;

    setState(prev => ({
      ...prev,
      isPlaying: false,
      currentIndex: 0,
      currentPosition: points.length > 0 ? [points[0].latitude, points[0].longitude] : null,
      currentTimestamp: points.length > 0 ? points[0].recorded_at : null,
      distanceCoveredKm: 0,
    }));
  }, [points]);

  const setSpeed = useCallback((speed: number) => {
    speedRef.current = speed;
    setState(prev => ({ ...prev, speed }));
  }, []);

  const seekTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= points.length) return;
      currentIndexRef.current = index;
      progressRef.current = 0;

      setState(prev => ({
        ...prev,
        currentIndex: index,
        currentPosition: [points[index].latitude, points[index].longitude],
        currentTimestamp: points[index].recorded_at,
        distanceCoveredKm: cumulativeDistances[index],
      }));
    },
    [points, cumulativeDistances]
  );

  // Cleanup
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      isPlayingRef.current = false;
    };
  }, []);

  return { playbackState: state, play, pause, stop, setSpeed, seekTo };
}
